/**
 * One motion vocabulary for the whole app (after Emil Kowalski and Apple's
 * "Designing Fluid Interfaces"):
 * - entrances use a strong ease-out, on-screen moves use ease-in-out
 * - UI stays under 300 ms; marketing moments may take longer
 * - springs are critically damped by default; bounce only after a gesture with momentum
 * - reduced motion is handled once by <MotionConfig reducedMotion="user"> in App
 */
import type { Transition, Variants } from 'motion/react';

export const EASE_OUT = [0.23, 1, 0.32, 1] as const;
export const EASE_IN_OUT = [0.77, 0, 0.175, 1] as const;
export const EASE_DRAWER = [0.32, 0.72, 0, 1] as const;

/** Critically damped: settles without overshoot */
export const SPRING: Transition = { type: 'spring', duration: 0.4, bounce: 0 };
/** For things that move because the user flicked or dragged them */
export const SPRING_MOMENTUM: Transition = { type: 'spring', duration: 0.45, bounce: 0.2 };

/** Viewport options for marketing reveals: fire once, a little before the fold */
export const IN_VIEW = { once: true, margin: '-80px' } as const;

/** Rise + unblur. The blur bridges the crossfade so it reads as one object arriving */
export const reveal: Variants = {
  hidden: { opacity: 0, y: 16, filter: 'blur(6px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.6, ease: EASE_OUT } },
};

/** Container that staggers its children; keep gaps in the 30–80 ms band */
export const stagger = (gap = 0.06, delay = 0): Variants => ({
  hidden: {},
  show: { transition: { staggerChildren: gap, delayChildren: delay } },
});
